import { useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { Button } from '@mui/material';
import { Sparkles } from 'lucide-react';
import { useFormContext } from 'react-hook-form';

import { gql } from '@graphql/__generated__';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useAnalytics } from '@shared/analytics';
import { ANALYTICS_EVENTS } from '@shared/analytics/constants';
import { useLocale } from '@shared/hooks/locale';

const GET_PROPERTY_DISTANCES = gql(/* GraphQL */ `
  mutation GetPropertyDistances($propertyId: uuid!) {
    get_property_distances(property_id: $propertyId) {
      success
      distances
    }
  }
`);

const DISTANCE_FIELDS = [
  'distance_to_airport',
  'distance_to_city_center',
  'distance_to_grocery_shop',
  'distance_to_high_school',
  'distance_to_hospital',
  'distance_to_kindergarten',
  'distance_to_motorway',
  'distance_to_primary_school',
  'distance_to_public_transport',
  'distance_to_sports_center',
  'distance_to_train_station',
] as const;

type FetchDistancesButtonProps = {
  propertyId: string;
};

export const FetchDistancesButton = ({
  propertyId,
}: FetchDistancesButtonProps) => {
  const { t } = useLocale();
  const { setValue } = useFormContext();
  const { track } = useAnalytics();
  const [error, setError] = useState<ErrorLike | null>(null);

  const [getDistances, { loading }] = useMutation(GET_PROPERTY_DISTANCES, {
    variables: { propertyId },
    onCompleted: data => {
      if (!data.get_property_distances?.success) {
        return;
      }

      const distances = data.get_property_distances.distances ?? {};

      for (const field of DISTANCE_FIELDS) {
        setValue(field, distances[field] ?? null, { shouldDirty: true });
      }
    },
    onError: setError,
  });

  return (
    <>
      <Button
        startIcon={<Sparkles />}
        loadingPosition="start"
        loading={loading}
        onClick={() => {
          track(ANALYTICS_EVENTS.AI_GENERATE_CLICKED, {
            source: 'fetch_distances',
            property_id: propertyId,
          });
          getDistances();
        }}
        variant="soft"
      >
        {t('Auto-fetch distances')}
      </Button>

      <MutationErrorSnackbar error={error} onClose={() => setError(null)} />
    </>
  );
};
